
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertCircle, CheckCircle2, Clock, XCircle, TestTube } from "lucide-react";
import { useWebhookConfiguration } from "@/hooks/useWebhookConfiguration";

const WebhookConfiguration = () => {
  const {
    webhookConfig,
    webhookLogs,
    isLoadingConfig,
    isLoadingLogs,
    isTestingWebhook,
    saveWebhookConfig,
    testWebhook,
  } = useWebhookConfiguration();

  const [webhookUrl, setWebhookUrl] = useState("");
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (webhookConfig) {
      setWebhookUrl(webhookConfig.webhook_url);
      setIsActive(webhookConfig.is_active);
    }
  }, [webhookConfig]);

  const handleSave = async () => {
    if (!webhookUrl.trim()) {
      return;
    }

    // Validar URL
    try {
      new URL(webhookUrl);
    } catch {
      alert('Por favor, insira uma URL válida');
      return;
    }

    await saveWebhookConfig.mutateAsync({
      webhook_url: webhookUrl,
      is_active: isActive,
    });
  };

  const handleTest = async () => {
    if (!webhookUrl.trim()) {
      alert('Por favor, configure uma URL primeiro');
      return;
    }

    await testWebhook(webhookUrl);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <Badge variant="default" className="bg-green-100 text-green-800"><CheckCircle2 className="h-3 w-3 mr-1" />Sucesso</Badge>;
      case 'failed':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Falha</Badge>;
      case 'pending':
        return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" />Pendente</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (isLoadingConfig) {
    return <div>Carregando configuração...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Configuração de Webhook
          </CardTitle>
          <CardDescription>
            Configure um webhook que será chamado automaticamente quando uma campanha for finalizada.
            Ideal para integrar com N8N e automatizar a desativação de cashback.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="webhook-url">URL do Webhook</Label>
            <Input
              id="webhook-url"
              type="url"
              placeholder="https://vello.app.n8n.cloud/webhook/81a43612-2b56-40d2-811f-215a3a01014d"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
            />
            <p className="text-sm text-gray-500">
              Insira a URL do seu webhook N8N ou outro serviço que receberá as notificações.
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="webhook-active"
              checked={isActive}
              onCheckedChange={setIsActive}
            />
            <Label htmlFor="webhook-active">Webhook ativo</Label>
          </div>

          <div className="flex gap-2">
            <Button 
              onClick={handleSave}
              disabled={saveWebhookConfig.isPending || !webhookUrl.trim()}
            >
              {saveWebhookConfig.isPending ? 'Salvando...' : 'Salvar Configuração'}
            </Button>
            
            <Button
              variant="outline"
              onClick={handleTest}
              disabled={isTestingWebhook || !webhookUrl.trim()}
            >
              <TestTube className="h-4 w-4 mr-2" />
              {isTestingWebhook ? 'Testando...' : 'Testar Webhook'}
            </Button>
          </div>

          <div className="bg-blue-50 p-3 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">Payload do Webhook</h4>
            <pre className="text-xs text-blue-800 overflow-x-auto">
{`{
  "event": "campaign.completed",
  "timestamp": "2024-07-08T12:00:00Z",
  "data": {
    "campaign_id": "uuid",
    "campaign_name": "Nome da Campanha",
    "new_status": "completed",
    "user_id": "uuid",
    "coupon_codes": ["CUPOM1", "CUPOM2"],
    "completed_at": "2024-07-08T12:00:00Z"
  }
}`}
            </pre>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Logs de Webhook</CardTitle>
          <CardDescription>
            Histórico de webhooks enviados para suas campanhas finalizadas.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingLogs ? (
            <div>Carregando logs...</div>
          ) : webhookLogs && webhookLogs.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data/Hora</TableHead>
                  <TableHead>Evento</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Dados</TableHead>
                  <TableHead>Erro</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {webhookLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="text-sm">
                      {new Date(log.created_at).toLocaleString('pt-BR')}
                    </TableCell>
                    <TableCell className="text-sm">
                      {log.event_type}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(log.status)}
                    </TableCell>
                    <TableCell className="text-sm max-w-xs">
                      {log.data?.data?.campaign_name || 'N/A'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {log.error && (
                        <div className="text-red-600 max-w-xs truncate" title={log.error}>
                          {log.error}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-gray-500">
              Nenhum webhook foi enviado ainda.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WebhookConfiguration;
